import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Globe, Landmark } from 'lucide-react';
import { publicRequest } from '../../requestMethods';
import './CitySearch.scss';

// City to district mapping for Kerala
const CITY_TO_DISTRICT_MAP = {
    'thrissur': 'thrissur',
    'trichur': 'thrissur',
    'kozhikode': 'kozhikode',
    'calicut': 'kozhikode',
    'ernakulam': 'ernakulam',
    'kochi': 'ernakulam',
    'cochin': 'ernakulam',
    'thiruvananthapuram': 'thiruvananthapuram',
    'trivandrum': 'thiruvananthapuram',
    'kollam': 'kollam',
    'quilon': 'kollam',
    'alappuzha': 'alappuzha',
    'alleppey': 'alappuzha',
    'kottayam': 'kottayam',
    'idukki': 'idukki',
    'palakkad': 'palakkad',
    'palghat': 'palakkad',
    'malappuram': 'malappuram',
    'kannur': 'kannur',
    'cannanore': 'kannur',
    'kasaragod': 'kasaragod',
    'wayanad': 'wayanad',
    'pathanamthitta': 'pathanamthitta'
};

const CitySearch = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // No need to load external scripts - using Nominatim API directly

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target) &&
                searchRef.current &&
                !searchRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const mapCityToDistrict = (cityName) => {
        const normalized = cityName.toLowerCase().trim();
        return CITY_TO_DISTRICT_MAP[normalized] || normalized;
    };

    const searchCities = async (query) => {
        if (query.length < 3) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsLoading(true);
        try {
            const queryLower = query.toLowerCase();

            // 1. Search mosque names
            const mosqueSearchPromise = publicRequest
                .get('/mosques/search-mosques', { params: { q: query, limit: 5 } })
                .then(res => (res.data?.mosques || []).map(m => ({
                    source: 'mosque',
                    type: 'mosque',
                    mosqueID: m.mosqueID,
                    name: m.name,
                    district: m.district,
                    place_id: `mosque-${m.mosqueID}`,
                    structured_formatting: {
                        main_text: m.name,
                        secondary_text: m.district || ''
                    }
                })))
                .catch(() => []);

            // 2. Search our mosque records (districts we have data for)
            const ourRecordsPromise = publicRequest
                .get('/mosques/search-districts', { params: { q: query, limit: 5 } })
                .then(res => (res.data?.districts || []).map(d => ({
                    source: 'our-records',
                    description: `${d.district} (${d.mosqueCount} mosque${d.mosqueCount !== 1 ? 's' : ''})`,
                    place_id: `our-${d.district}`,
                    lat: d.lat,
                    lon: d.lon,
                    district: d.district,
                    mosqueCount: d.mosqueCount,
                    structured_formatting: {
                        main_text: d.district,
                        secondary_text: `${d.mosqueCount} mosque${d.mosqueCount !== 1 ? 's' : ''} in our records`
                    }
                })))
                .catch(() => []);

            // 2. Search Nominatim (OpenStreetMap / map search)
            const mapSearchPromise = (async () => {
                const keralaCities = Object.keys(CITY_TO_DISTRICT_MAP);
                const filteredCities = keralaCities
                    .filter(city => city.toLowerCase().startsWith(queryLower) || city.toLowerCase().includes(queryLower))
                    .sort((a, b) => {
                        const aStarts = a.toLowerCase().startsWith(queryLower);
                        const bStarts = b.toLowerCase().startsWith(queryLower);
                        if (aStarts && !bStarts) return -1;
                        if (!aStarts && bStarts) return 1;
                        return a.localeCompare(b);
                    })
                    .slice(0, 3);

                const localMatches = await Promise.all(filteredCities.map(async (city) => {
                    try {
                        const res = await fetch(
                            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city + ', Kerala, India')}&limit=1`,
                            { headers: { 'User-Agent': 'ChorullaPalli/1.0' } }
                        );
                        const data = await res.json();
                        const coords = data.length > 0 ? { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) } : null;
                        return {
                            source: 'map',
                            description: city.charAt(0).toUpperCase() + city.slice(1) + ', Kerala, India',
                            place_id: `map-${city}`,
                            lat: coords?.lat,
                            lon: coords?.lon,
                            structured_formatting: {
                                main_text: city.charAt(0).toUpperCase() + city.slice(1),
                                secondary_text: 'Kerala, India (map search)'
                            }
                        };
                    } catch {
                        return {
                            source: 'map',
                            description: city.charAt(0).toUpperCase() + city.slice(1) + ', Kerala, India',
                            place_id: `map-${city}`,
                            structured_formatting: {
                                main_text: city.charAt(0).toUpperCase() + city.slice(1),
                                secondary_text: 'Kerala, India'
                            }
                        };
                    }
                }));

                if (localMatches.length > 0) return localMatches;

                const response = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Kerala, India')}&limit=5&addressdetails=1`,
                    { headers: { 'User-Agent': 'ChorullaPalli/1.0' } }
                );
                if (!response.ok) return [];
                const data = await response.json();
                return data
                    .filter(item => {
                        const type = (item.type || '').toLowerCase();
                        const displayName = (item.display_name || '').toLowerCase();
                        const isPlace = type.includes('city') || type.includes('town') || type.includes('administrative') || type.includes('village');
                        const inKerala = displayName.includes('kerala') || keralaCities.some(c => displayName.includes(c));
                        const notBusiness = !type.includes('shop') && !type.includes('restaurant') && !type.includes('amenity');
                        return isPlace && inKerala && notBusiness;
                    })
                    .slice(0, 5)
                    .map(item => {
                        const parts = (item.display_name || '').split(',');
                        const cityName = parts[0].trim();
                        return {
                            source: 'map',
                            description: item.display_name,
                            place_id: `map-${item.place_id || item.osm_id}`,
                            lat: parseFloat(item.lat),
                            lon: parseFloat(item.lon),
                            structured_formatting: {
                                main_text: cityName,
                                secondary_text: parts.slice(1).join(',').trim() || 'Kerala, India'
                            }
                        };
                    });
            })();

            const [mosqueResults, ourRecords, mapResults] = await Promise.all([
                mosqueSearchPromise,
                ourRecordsPromise,
                mapSearchPromise
            ]);

            // Deduplicate: if district from our records appears in map, prefer our record
            const seenDistricts = new Set(ourRecords.map(s => (s.district || s.structured_formatting?.main_text || '').toLowerCase()));
            const uniqueMapResults = mapResults.filter(s => {
                const name = (s.structured_formatting?.main_text || '').toLowerCase();
                if (seenDistricts.has(name)) return false;
                seenDistricts.add(name);
                return true;
            });

            // Mosques first (most specific), then districts, then map results
            const combined = [...mosqueResults, ...ourRecords, ...uniqueMapResults];
            setSuggestions(combined);
            setShowSuggestions(combined.length > 0);
        } catch (error) {
            console.error('Error searching cities:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Clear previous debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Debounce search
        debounceTimerRef.current = setTimeout(() => {
            if (value.length >= 3) {
                searchCities(value);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion.name || suggestion.district || suggestion.structured_formatting?.main_text || '');
        setShowSuggestions(false);
        setSuggestions([]);

        if (suggestion.type === 'mosque' && suggestion.mosqueID) {
            navigate(`/mosque/${suggestion.mosqueID}`);
            return;
        }

        const cityName = suggestion.district || suggestion.structured_formatting?.main_text || 
                        suggestion.description?.split(',')[0]?.trim() || '';
        const district = mapCityToDistrict(cityName.toLowerCase());
        
        if (suggestion.lat != null && suggestion.lon != null) {
            navigate(`/${district.toLowerCase()}?city=${encodeURIComponent(cityName)}&lat=${suggestion.lat}&lon=${suggestion.lon}`);
        } else {
            navigate(`/${district.toLowerCase()}?city=${encodeURIComponent(cityName)}`);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && suggestions.length > 0) {
            handleSuggestionClick(suggestions[0]);
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <div className="city-search" ref={searchRef}>
            <div className="city-search__input-wrapper">
                <span className="city-search__prefix">chorullapalli.com/</span>
                <Search className="city-search__icon" size={20} />
                <input
                    type="text"
                    className="city-search__input"
                    placeholder="Search mosque or city (min 3 characters)..."
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => {
                        if (suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                />
                {isLoading && (
                    <div className="city-search__loader">
                        <div className="spinner"></div>
                    </div>
                )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
                <div className="city-search__suggestions" ref={suggestionsRef}>
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={suggestion.place_id || index}
                            className="city-search__suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion.source === 'mosque' ? (
                                <Landmark className="city-search__suggestion-icon city-search__suggestion-icon--mosque" size={18} />
                            ) : suggestion.source === 'our-records' ? (
                                <Building2 className="city-search__suggestion-icon city-search__suggestion-icon--records" size={18} />
                            ) : (
                                <Globe className="city-search__suggestion-icon city-search__suggestion-icon--map" size={18} />
                            )}
                            <div className="city-search__suggestion-content">
                                <div className="city-search__suggestion-main">
                                    {suggestion.name || suggestion.district || suggestion.structured_formatting?.main_text || 
                                     suggestion.description?.split(',')[0]}
                                </div>
                                <div className="city-search__suggestion-secondary">
                                    {suggestion.structured_formatting?.secondary_text || 
                                     suggestion.description?.split(',').slice(1).join(',').trim() || ''}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CitySearch;
